import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { URL_GET_TIMEZONES, URL_WEIGHERS } from 'services/urls';
import API from 'services/api.js';

const fetchWeigher = (weigher_id) => {
	return API.get(URL_WEIGHERS, { params: { id: weigher_id } }).then((resp) => {
		if (resp.data && resp.data.length === 1) {
			return resp.data[0];
		}
		throw new Error(`Weigher ${weigher_id} not found`);
	});
};

export const useWeigherModel = (weigher_id) => {
	const currentUser = useSelector((state) => state.currentUser);
	const has_api_token = currentUser.username.length > 3;
	const [name, set_name] = useState(null);
	const [timezone, set_timezone] = useState(null);
	const { isLoading, isError, data, error, isFetching } = useQuery(
		['weigher', weigher_id],
		() => fetchWeigher(weigher_id),
		{ enabled: has_api_token, refetchOnWindowFocus: true }
	);
	const timezones = useQuery(
		'timezone-list',
		() => {
			return API.get(URL_GET_TIMEZONES).then((resp) => {
				if (resp.data && Array.isArray(resp.data)) {
					return resp.data.map((tz) => tz.timezone);
				}
				throw new Error('bad timezones');
			});
		},
		{ enabled: has_api_token }
	);
	const queryClient = useQueryClient();
	const post = useMutation(
		() => {
			const body = {};
			if (name != null) body.name = name;
			if (timezone != null) body.defaultTimezone = timezone;
			return API.post(URL_WEIGHERS, body, {
				params: {
					id: weigher_id,
				},
			});
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries('weigher');
				set_name(null);
				set_timezone(null);
			},
		}
	);
	const isSaving = post.isLoading || isFetching;
	const saveHandler = post.mutate;

	// prefill the name and timezone changes
	useEffect(() => {
		if (data) {
			if (!data.name) set_name(`Unit ${weigher_id}`);

			if (
				data.Species === undefined && // is new
				data.changes &&
				data.changes.defaultTimezone
			)
				set_timezone(data.changes.defaultTimezone);
		}
	}, [weigher_id, data]);

	if (!has_api_token)
		return {
			isLoading: true,
		};

	if (isLoading || isError) {
		return { isLoading, isError, error };
	}

	const crmLink = data.link;
	const isNew = data.Species === undefined;

	const hasChanges =
		name != null || timezone != null || Object.keys(data.changes).length > 0;
	const canSave =
		hasChanges &&
		(data.changes.client === undefined || data.changes.client.id != null);

	const client = {
		label: 'Client',
		current: { text: data.client.name, link: data.client.link },
		update: data.changes.client
			? {
					text: data.changes.client.name,
					link: data.changes.client.link,
					error:
						data.changes.client.id !== null
							? null
							: data.changes.client.link
							? 'Not found in database, click to add'
							: 'Not assigned to a customer/account',
			  }
			: null,
	};

	const crmValues = [
		client,
		{
			label: 'Species',
			current: { text: data.Species },
			update: data.changes.Species ? { text: data.changes.Species } : undefined,
		},
		{
			label: 'Swarm',
			current: { text: data.SatModem },
			update: data.changes.SatModem ? { text: data.changes.SatModem } : null,
		},
	];
	const userValues = [
		{
			label: 'Name',
			current: data.name,
			update: name,
			setValue: set_name,
		},
		{
			label: 'Timezone',
			current: data.defaultTimezone,
			options: timezones.data,
			update: timezone,
			setValue: set_timezone,
		},
	];
	return {
		isLoading: false,
		isError: false,
		crmLink,
		isNew,
		canSave,
		crmValues,
		userValues,
		isSaving,
		saveHandler,
	};
};
